import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Flex,
  Input,
  Select,
  Space,
  Spin,
  Typography,
  Upload,
  message,
} from "antd";
import type { UploadFile } from "antd";
import { LinkOutlined, LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { axiosInstance } from "@/utilities";
import { API_URL_PIM_PRODUCTS } from "@/services/XCONS_ENV";
import type {
  CatalogSearchResponse,
  LinkKind,
  ProductAttachment,
  ProductLink,
} from "./types";

/**
 * Los cuatro vínculos entre productos, **en el orden y con las etiquetas del Excel de Maxi**
 * (filas 46-49). La clave es la del guardado: `related`, `crosssell` y `upsell` son los
 * link types de Magento, y el Excel les puso un significado de negocio a cada uno.
 *
 * Los tres primeros son **alternativas** (otra presentación, otra marca, otro producto para el
 * mismo uso). `complementary` responde otra pregunta: **lo que se compra junto** —al cemento,
 * la cal y la arena—. Es un link type propio que crea un data patch de ViviendaVerde
 * (xcons-docs#476), y va último por eso.
 */
export const VINCULOS: Array<{ kind: LinkKind; label: string }> = [
  {
    kind: "related",
    label: "Mismo producto, igual marca (distinta presentación)",
  },
  {
    kind: "crosssell",
    label: "Mismo producto en otras marcas y presentaciones",
  },
  { kind: "upsell", label: "Otros productos similares para el mismo uso" },
  { kind: "complementary", label: "Productos complementarios" },
];

export const SIN_VINCULOS: Record<LinkKind, ProductLink[]> = {
  related: [],
  crosssell: [],
  upsell: [],
  complementary: [],
};

/** `3839807` → `3,7 MB`. */
export const formatoTamano = (bytes: number) => {
  if (!bytes) return "";
  const unidades = ["B", "KB", "MB", "GB"];
  let valor = bytes;
  let i = 0;
  while (valor >= 1024 && i < unidades.length - 1) {
    valor /= 1024;
    i += 1;
  }
  return `${valor.toLocaleString("es-AR", { maximumFractionDigits: 1 })} ${
    unidades[i]
  }`;
};

/**
 * Un link que se puede dibujar como `href`. React 18 no bloquea `javascript:`, y los links
 * de adjuntos y videos que ya están en la base los cargó cualquiera desde Magento: sin esto,
 * uno así ejecutaba código al clickearlo en el admin. Los nuevos ya los valida el API.
 */
export const esLinkSeguro = (url?: string | null): url is string =>
  !!url && /^https?:\/\//i.test(url.trim());

export const etiquetaDeProducto = (p: ProductLink) =>
  p.sku ? `${p.sku}${p.name ? ` · ${p.name}` : ""}` : `#${p.productId}`;

/**
 * Un buscador remoto con debounce. Lo comparten los dos selectores: la diferencia es qué
 * endpoint pega y cómo arma cada opción.
 */
function useBusqueda<T>(buscar: (texto: string) => Promise<T[]>) {
  const [opciones, setOpciones] = useState<T[]>([]);
  const [buscando, setBuscando] = useState(false);
  /**
   * Qué mostrar cuando no hay opciones. Con `notFoundContent={null}` el desplegable ni se
   * abría: una búsqueda vacía y una que falló se veían igual —como si no hubiera pasado
   * nada—, y no había forma de saber cuál de las dos era.
   */
  const [vacio, setVacio] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const ultima = useRef(0);

  useEffect(() => () => clearTimeout(timer.current), []);

  const onSearch = (texto: string) => {
    clearTimeout(timer.current);
    // **Un texto vacío no borra los resultados.** Al elegir una opción, antd limpia lo
    // tipeado y dispara `onSearch("")`: vaciar la lista ahí hacía desaparecer la opción
    // debajo del mouse, y el primer click no la agregaba —había que hacer dos—.
    if (!texto.trim()) {
      setVacio(null);
      return;
    }
    if (texto.trim().length < 2) {
      setOpciones([]);
      setVacio("Escribí al menos 2 letras");
      return;
    }
    timer.current = setTimeout(async () => {
      // Una respuesta vieja no pisa a una nueva: el operador sigue tipeando mientras la
      // primera búsqueda vuelve.
      const esta = ++ultima.current;
      setBuscando(true);
      try {
        const resultado = await buscar(texto.trim());
        if (esta === ultima.current) {
          setOpciones(resultado);
          setVacio(resultado.length ? null : "Sin resultados");
        }
      } catch {
        if (esta === ultima.current) {
          setOpciones([]);
          setVacio("No se pudo buscar");
        }
      } finally {
        if (esta === ultima.current) setBuscando(false);
      }
    }, 300);
  };

  return { opciones, buscando, vacio, onSearch };
}

/**
 * Los productos de un vínculo. Busca en el índice del catálogo —el mismo del listado—, así
 * que sólo ofrece productos **publicados**: un borrador no existe en Magento y no se puede
 * vincular. El propio producto se excluye.
 */
export const SelectorDeProductos: React.FC<{
  value: ProductLink[];
  onChange: (links: ProductLink[]) => void;
  excluirProductId?: number;
  disabled?: boolean;
}> = ({ value, onChange, excluirProductId, disabled }) => {
  const { opciones, buscando, vacio, onSearch } = useBusqueda<ProductLink>(
    async (search) => {
      const { data } = await axiosInstance.get<CatalogSearchResponse>(
        API_URL_PIM_PRODUCTS,
        // `page` es 1-based, como en el listado: el DTO tiene `@Min(1)` y con 0 daba 400.
        { params: { search, hitsPerPage: 10, page: 1 } },
      );
      return (data.hits ?? [])
        .filter((h) => h.magentoProductId !== excluirProductId)
        .map((h) => ({
          productId: h.magentoProductId,
          sku: h.sku,
          name: h.name,
        }));
    },
  );

  const conocidos = new Map(
    [...value, ...opciones].map((p) => [p.productId, p]),
  );

  return (
    <Select
      mode="multiple"
      labelInValue
      showSearch
      filterOption={false}
      // **Sin lista virtual.** Con la virtual, presionar una opción cortada en el borde
      // la desplazaba a la vista entre el mousedown y el click: el click caía en otro
      // lado y había que hacer dos. Son 10 resultados como mucho; no hace falta.
      virtual={false}
      disabled={disabled}
      style={{ width: "100%" }}
      placeholder="Buscar por SKU o nombre"
      notFoundContent={buscando ? <Spin size="small" /> : vacio}
      onSearch={onSearch}
      value={value.map((p) => ({
        value: p.productId,
        label: etiquetaDeProducto(p),
      }))}
      onChange={(elegidos: Array<{ value: number }>) =>
        onChange(
          elegidos.map((e, i) =>
            // Se conserva lo que ya se sabía del producto (SKU, nombre) y la posición
            // pasa a ser el orden en pantalla, que es lo que el operador ve.
            ({
              ...(conocidos.get(Number(e.value)) ?? {
                productId: Number(e.value),
              }),
              position: i,
            }),
          ),
        )
      }
      options={opciones.map((p) => ({
        value: p.productId,
        label: etiquetaDeProducto(p),
      }))}
    />
  );
};

/**
 * Las fichas técnicas de MageWorx. **Se asocian las que existen**: subir un adjunto nuevo
 * es del admin de Magento, que es el que lo guarda en su módulo.
 */
export const SelectorDeAdjuntos: React.FC<{
  value: ProductAttachment[];
  onChange: (adjuntos: ProductAttachment[]) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const { opciones, buscando, vacio, onSearch } = useBusqueda<ProductAttachment>(
    async (search) => {
      const { data } = await axiosInstance.get<ProductAttachment[]>(
        `${API_URL_PIM_PRODUCTS}/attachments`,
        { params: { search } },
      );
      return data ?? [];
    },
  );

  const conocidos = new Map(
    [...value, ...opciones].map((a) => [a.attachmentId, a]),
  );
  const etiqueta = (a: ProductAttachment) =>
    a.name ?? `Adjunto #${a.attachmentId}`;

  return (
    <Select
      mode="multiple"
      labelInValue
      showSearch
      filterOption={false}
      // **Sin lista virtual.** Con la virtual, presionar una opción cortada en el borde
      // la desplazaba a la vista entre el mousedown y el click: el click caía en otro
      // lado y había que hacer dos. Son 10 resultados como mucho; no hace falta.
      virtual={false}
      disabled={disabled}
      style={{ width: "100%" }}
      placeholder="Buscar la ficha por nombre o archivo"
      notFoundContent={buscando ? <Spin size="small" /> : vacio}
      onSearch={onSearch}
      value={value.map((a) => ({ value: a.attachmentId, label: etiqueta(a) }))}
      onChange={(elegidos: Array<{ value: number }>) =>
        onChange(
          elegidos.map(
            (e) =>
              conocidos.get(Number(e.value)) ?? {
                attachmentId: Number(e.value),
                name: null,
                type: null,
                size: 0,
                isActive: true,
                url: "",
              },
          ),
        )
      }
      options={opciones.map((a) => ({
        value: a.attachmentId,
        label: `${etiqueta(a)}${a.type ? ` (${a.type})` : ""}`,
      }))}
    />
  );
};

/**
 * Abre un adjunto subido **a través del API**, como las imágenes: el admin no depende de
 * que Magento sirva la media, y en local (sin `base_media_url`) es la única forma. Un
 * `<a href>` no manda el `Authorization`, así que se pide como blob.
 */
export async function abrirAdjunto(attachmentId: number) {
  // La pestaña se abre ANTES del await: abierta después, el navegador la toma como popup.
  const ventana = window.open("", "_blank");
  try {
    const { data } = await axiosInstance.get(
      `${API_URL_PIM_PRODUCTS}/attachments/${attachmentId}/file`,
      { responseType: "blob" },
    );
    // **Sólo PDF e imágenes se abren en pestaña.** Un blob abierto corre con el origen del
    // admin: si fuera HTML o SVG, ejecutaría código con la sesión del operador. El API ya
    // fija el tipo por extensión; esto es la segunda capa. Lo demás se descarga.
    const tipo = (data as Blob).type;
    const seMuestra = ["application/pdf", "image/jpeg", "image/png"].includes(tipo);
    const url = URL.createObjectURL(
      seMuestra ? (data as Blob) : new Blob([data as Blob], { type: "application/octet-stream" }),
    );
    if (seMuestra) {
      if (ventana) ventana.location.href = url;
      else window.open(url, "_blank");
    } else {
      ventana?.close();
      const a = document.createElement("a");
      a.href = url;
      a.download = `adjunto-${attachmentId}`;
      a.click();
    }
    // Se libera después de que la pestaña (o la descarga) lo tomó.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch {
    ventana?.close();
    message.error("No se pudo abrir el adjunto");
  }
}

/** El tope de un adjunto, el que dice el Figma (xcons-docs#490). */
const ADJUNTO_MAX_MB = 10;

/** Los tipos que tienen vista previa en la tarjeta: el resto muestra el ícono del archivo. */
const ES_IMAGEN = /^(jpe?g|png|gif|webp)$/i;

/**
 * Las miniaturas de los adjuntos que son imagen, `attachmentId → URL`. Un archivo subido se
 * pide por el API como blob —un `<img>` no manda el `Authorization`—; uno recién subido usa
 * el archivo local, que ya está en el navegador.
 */
const useMiniaturas = (
  adjuntos: ProductAttachment[],
  locales: Record<number, string>,
) => {
  const [remotas, setRemotas] = useState<Record<number, string>>({});
  const pedidas = useRef(new Set<number>());

  useEffect(() => {
    adjuntos
      .filter(
        (a) =>
          a.hasFile &&
          ES_IMAGEN.test(a.type ?? "") &&
          !locales[a.attachmentId] &&
          !pedidas.current.has(a.attachmentId),
      )
      .forEach((a) => {
        pedidas.current.add(a.attachmentId);
        axiosInstance
          .get(`${API_URL_PIM_PRODUCTS}/attachments/${a.attachmentId}/file`, {
            responseType: "blob",
          })
          .then(({ data }) =>
            setRemotas((prev) => ({
              ...prev,
              [a.attachmentId]: URL.createObjectURL(data as Blob),
            })),
          )
          // Sin miniatura no se pierde nada: la tarjeta queda con el ícono.
          .catch(() => undefined);
      });
  }, [adjuntos, locales]);

  // Las URLs de blob se liberan al salir de la ficha.
  const vivas = useRef(remotas);
  vivas.current = remotas;
  useEffect(
    () => () => Object.values(vivas.current).forEach((u) => URL.revokeObjectURL(u)),
    [],
  );

  return { ...remotas, ...locales };
};

/**
 * **Los adjuntos de la ficha**: subir un archivo o cargar un link, sin pasar por el admin de
 * Magento. Lo creado se suma a la selección; se asocia al producto al guardar.
 *
 * Como las facturas de Pedidos (xcons-docs#490): cada adjunto es una tarjeta al lado de
 * "+ Subir", con la vista previa si es imagen, el ojo para abrirlo y el tacho para quitarlo
 * del producto. El tope va abajo, como en el Figma, y el link debajo, ya abierto: es la otra
 * forma de cargar una ficha (xcons-docs#432).
 */
export const AdjuntosEditables: React.FC<{
  adjuntos: ProductAttachment[];
  onCreado: (adjunto: ProductAttachment) => void;
  onQuitar: (adjunto: ProductAttachment) => void;
  disabled?: boolean;
}> = ({ adjuntos, onCreado, onQuitar, disabled }) => {
  const [subiendo, setSubiendo] = useState(false);
  /** Separado de la subida: el spinner va en el botón que se tocó. */
  const [guardandoLink, setGuardandoLink] = useState(false);
  const [nombre, setNombre] = useState("");
  const [link, setLink] = useState("");
  /** La vista previa de lo que se subió recién, desde el archivo local. */
  const [locales, setLocales] = useState<Record<number, string>>({});
  const miniaturas = useMiniaturas(adjuntos, locales);

  const error = (e: unknown, porDefecto: string) =>
    message.error(
      (e as { response?: { data?: { message?: string } } })?.response?.data
        ?.message ?? porDefecto,
    );

  const subir = async (file: File) => {
    if (file.size > ADJUNTO_MAX_MB * 1024 * 1024) {
      message.error(`"${file.name}" pesa más de ${ADJUNTO_MAX_MB} MB`);
      return false;
    }
    setSubiendo(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axiosInstance.post<ProductAttachment>(
        `${API_URL_PIM_PRODUCTS}/attachments`,
        form,
        // Igual que la subida de imágenes: el default del cliente es JSON.
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (file.type.startsWith("image/")) {
        setLocales((prev) => ({
          ...prev,
          [data.attachmentId]: URL.createObjectURL(file),
        }));
      }
      onCreado(data);
      message.success(`"${data.name}" subido. Se asocia al guardar.`);
    } catch (e) {
      error(e, "No se pudo subir el adjunto");
    } finally {
      setSubiendo(false);
    }
    // `false`: el upload lo hace este handler, no antd.
    return false;
  };

  const guardarLink = async () => {
    setGuardandoLink(true);
    try {
      const { data } = await axiosInstance.post<ProductAttachment>(
        `${API_URL_PIM_PRODUCTS}/attachments`,
        { name: nombre, url: link },
      );
      onCreado(data);
      setNombre("");
      setLink("");
      message.success(`"${data.name}" agregado. Se asocia al guardar.`);
    } catch (e) {
      error(e, "No se pudo agregar el link");
    } finally {
      setGuardandoLink(false);
    }
  };

  const porUid = new Map(adjuntos.map((a) => [String(a.attachmentId), a]));
  const tarjetas: UploadFile[] = adjuntos.map((a) => ({
    uid: String(a.attachmentId),
    name: a.name ?? `Adjunto #${a.attachmentId}`,
    status: "done",
    thumbUrl: miniaturas[a.attachmentId],
    // Un link sin archivo se abre directo; uno subido, por el API (`onPreview`).
    url: !a.hasFile && esLinkSeguro(a.url) ? a.url : undefined,
  }));

  return (
    <Flex vertical gap={8}>
      <div>
        <Upload
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.dwg,.zip"
          listType="picture-card"
          fileList={tarjetas}
          beforeUpload={subir}
          onPreview={(f) => {
            const a = porUid.get(f.uid);
            if (a?.hasFile) void abrirAdjunto(a.attachmentId);
            else if (a && esLinkSeguro(a.url)) window.open(a.url, "_blank", "noreferrer");
          }}
          // Sin confirmar, como la galería: se saca del producto recién al guardar.
          onRemove={(f) => {
            const a = porUid.get(f.uid);
            if (a) onQuitar(a);
            return false;
          }}
          showUploadList={{ showPreviewIcon: true, showRemoveIcon: !disabled }}
          disabled={disabled || subiendo}
          multiple
        >
          <Flex vertical align="center" gap={8}>
            {subiendo ? <LoadingOutlined /> : <PlusOutlined />}
            <span>Subir</span>
          </Flex>
        </Upload>
        <Typography.Text
          type="secondary"
          style={{ display: "block", fontSize: 12 }}
        >
          Máximo {ADJUNTO_MAX_MB} MB
        </Typography.Text>
      </div>
      {/* El link, **abierto y listo para usar** (xcons-docs#490): sin el botón que lo
          desplegaba. */}
      <Space.Compact style={{ width: "100%" }}>
        <Input
          prefix={<LinkOutlined />}
          placeholder="Nombre (ej. Ficha técnica)"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          style={{ width: "35%" }}
        />
        <Input
          placeholder="https://…"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          onPressEnter={() => void guardarLink()}
        />
        <Button
          type="primary"
          loading={guardandoLink}
          disabled={disabled || !nombre.trim() || !link.trim()}
          onClick={() => void guardarLink()}
        >
          Agregar
        </Button>
      </Space.Compact>
    </Flex>
  );
};
